import React, { useState, useEffect, useCallback } from 'react';
import client from '../../api/client';
import AdminTable from '../../components/Admin/AdminTable';
import ExcelUploadModal from '../../components/Admin/ExcelUploadModal';

const SAMPLE_COLS = ['location_id','name','city','state','sla_class','re_count','spoc_name','spoc_phone','spoc_email','contract_start_date'];

const EMPTY_FORM = { location_id:'', name:'', city:'', state:'', sla_class:'A', re_count:'', spoc_name:'', spoc_phone:'', spoc_email:'', contract_start_date:'', is_active:true };

const TABLE_COLS = [
  { key:'location_id', label:'Location ID' },
  { key:'name', label:'Name' },
  { key:'city', label:'City' },
  { key:'state', label:'State' },
  { key:'sla_class', label:'SLA Class' },
  { key:'re_count', label:'RE Count' },
  { key:'spoc_name', label:'SPOC Name' },
  { key:'is_active', label:'Active', render: v => (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${v ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>{v ? 'Yes' : 'No'}</span>
  )},
];

export default function Locations() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showUpload, setShowUpload] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [deleteRow, setDeleteRow] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: res } = await client.get('/admin/locations/');
      setData(Array.isArray(res) ? res : res.results ?? []);
    } catch { setError('Failed to load locations.'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => { setEditRow(null); setForm(EMPTY_FORM); setShowForm(true); };
  const openEdit = (row) => { setEditRow(row); setForm({ ...row, contract_start_date: row.contract_start_date || '' }); setShowForm(true); };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        re_count: form.re_count === '' || form.re_count == null ? 0 : Number(form.re_count),
        contract_start_date: form.contract_start_date === '' ? null : form.contract_start_date,
      };
      if (editRow) {
        await client.put(`/admin/locations/${editRow.id}/`, payload);
      } else {
        await client.post('/admin/locations/', payload);
      }
      setShowForm(false);
      load();
    } catch (err) {
      const data = err?.response?.data;
      const message = data?.detail
        || (data && typeof data === 'object' ? Object.values(data).flat().join(' ') : null)
        || 'Save failed.';
      alert(message);
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try {
      await client.delete(`/admin/locations/${deleteRow.id}/`);
      setDeleteRow(null);
      load();
    } catch { alert('Delete failed.'); }
  };

  const handleUpload = async (file) => {
    const fd = new FormData(); fd.append('file', file);
    const { data: res } = await client.post('/admin/upload/locations/', fd);
    load();
    return res;
  };

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const inp = 'border border-gray-200 rounded-xl px-3 py-2 text-sm w-full focus:outline-none focus:ring-2 focus:ring-purple-300';

  return (
    <div className="p-6 space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-800">Locations</h1>
          <p className="text-sm text-gray-500 mt-0.5">Manage BHEL site locations</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setShowUpload(true)} className="flex items-center gap-2 px-4 py-2 text-sm rounded-xl border border-gray-200 text-gray-700 hover:bg-gray-50 font-medium">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" /></svg>
            Upload Excel
          </button>
          <button onClick={openAdd} className="flex items-center gap-2 px-4 py-2 text-sm rounded-xl bg-[#C1278D] hover:bg-[#a01f75] text-white font-semibold">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" /></svg>
            Add Location
          </button>
        </div>
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-4 py-3">{error}</div>}

      {/* Table Card */}
      <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
        <AdminTable columns={TABLE_COLS} data={data} loading={loading} onEdit={openEdit} onDelete={setDeleteRow} />
      </div>

      {/* Upload Modal */}
      {showUpload && (
        <ExcelUploadModal title="Locations" onUpload={handleUpload} onClose={() => setShowUpload(false)} sampleColumns={SAMPLE_COLS} templateEntity="locations" />
      )}

      {/* Add/Edit Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <h2 className="text-base font-semibold text-gray-800">{editRow ? 'Edit Location' : 'Add Location'}</h2>
              <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-600">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
            <form onSubmit={handleSave} className="p-6 grid grid-cols-2 gap-4">
              <div><label className="text-xs font-medium text-gray-600 mb-1 block">Location ID *</label><input className={inp} value={form.location_id} onChange={e => set('location_id', e.target.value)} required /></div>
              <div><label className="text-xs font-medium text-gray-600 mb-1 block">Name *</label><input className={inp} value={form.name} onChange={e => set('name', e.target.value)} required /></div>
              <div><label className="text-xs font-medium text-gray-600 mb-1 block">City</label><input className={inp} value={form.city} onChange={e => set('city', e.target.value)} /></div>
              <div><label className="text-xs font-medium text-gray-600 mb-1 block">State</label><input className={inp} value={form.state} onChange={e => set('state', e.target.value)} /></div>
              <div><label className="text-xs font-medium text-gray-600 mb-1 block">SLA Class</label>
                <select className={inp} value={form.sla_class} onChange={e => set('sla_class', e.target.value)}>
                  <option value="A">A</option><option value="B">B</option>
                </select>
              </div>
              <div><label className="text-xs font-medium text-gray-600 mb-1 block">RE Count</label><input type="number" className={inp} value={form.re_count} onChange={e => set('re_count', e.target.value)} /></div>
              <div><label className="text-xs font-medium text-gray-600 mb-1 block">SPOC Name</label><input className={inp} value={form.spoc_name} onChange={e => set('spoc_name', e.target.value)} /></div>
              <div><label className="text-xs font-medium text-gray-600 mb-1 block">SPOC Phone</label><input className={inp} value={form.spoc_phone} onChange={e => set('spoc_phone', e.target.value)} /></div>
              <div><label className="text-xs font-medium text-gray-600 mb-1 block">SPOC Email</label><input type="email" className={inp} value={form.spoc_email} onChange={e => set('spoc_email', e.target.value)} /></div>
              <div><label className="text-xs font-medium text-gray-600 mb-1 block">Contract Start Date</label><input type="date" className={inp} value={form.contract_start_date} onChange={e => set('contract_start_date', e.target.value)} /></div>
              <div className="col-span-2 flex items-center gap-2">
                <input type="checkbox" id="loc_active" checked={!!form.is_active} onChange={e => set('is_active', e.target.checked)} className="w-4 h-4 accent-purple-600" />
                <label htmlFor="loc_active" className="text-sm text-gray-700">Active</label>
              </div>
              <div className="col-span-2 flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 text-sm rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-50">Cancel</button>
                <button type="submit" disabled={saving} className="px-4 py-2 text-sm rounded-xl bg-[#C1278D] hover:bg-[#a01f75] text-white font-semibold disabled:opacity-50">{saving ? 'Savingâ€¦' : 'Save'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirm */}
      {deleteRow && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6 space-y-4">
            <h3 className="text-base font-semibold text-gray-800">Delete Location</h3>
            <p className="text-sm text-gray-600">Are you sure you want to delete <strong>{deleteRow.name}</strong>? This cannot be undone.</p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setDeleteRow(null)} className="px-4 py-2 text-sm rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-50">Cancel</button>
              <button onClick={handleDelete} className="px-4 py-2 text-sm rounded-xl bg-red-500 hover:bg-red-600 text-white font-semibold">Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

